<template>
  <form data-testid="ask-a-question-form" @submit.prevent="onSubmit">
    <template v-if="isLoggedInUser">
      <p class="mb-4 text-sm font-medium text-gray-800" data-testid="ask-a-question-form-send-reply-to">
        Send reply to:&nbsp;<span class="font-bold text-gray-900">{{ (user as User).email_address }}</span>
      </p>
    </template>

    <template v-else>
      <div class="mb-4">
        <label class="mb-1 text-sm font-medium text-gray-800">Full Name</label>
        <VInput
          v-bind="nameAttrs"
          v-model="name"
          type="text"
          name="name"
          :error="!!errors.name"
          data-testid="ask-a-question-name-input"
          :maxlength="120"
        />
        <div v-if="errors.name" class="mt-1 text-sm text-red-800">{{ errors.name }}</div>
      </div>

      <div v-if="guestHasExistingEmailAddress && !isEditingEmail" class="flex">
        <p class="mb-4 text-sm font-medium text-gray-800" data-testid="aaq-email">
          Send reply to:&nbsp;<span class="font-bold text-gray-900">{{ existingEmailAddress }}</span>
        </p>
        <a
          class="ml-3 text-sm text-black underline cursor-pointer edit-btn hover_text-gray-600 hover_text-black"
          data-testid="aaq-edit-email-btn"
          @click="onEditExistingEmail"
        >
          Edit
        </a>
      </div>
      <div v-if="!guestHasExistingEmailAddress || isEditingEmail" class="mb-4">
        <label class="mb-1 text-sm font-medium text-gray-800" @click="onEditExistingEmail">Email</label>
        <VInput
          ref="emailInputRef"
          v-bind="emailAttrs"
          v-model="email"
          type="email"
          name="email"
          :error="!!errors.email"
          :maxlength="120"
          data-testid="ask-a-question-email-input"
        />
        <div v-if="errors.email" class="mt-1 text-sm text-red-800">{{ errors.email }}</div>
      </div>
    </template>

    <div class="mb-6">
      <label class="mb-1 text-sm font-medium text-gray-800">Question</label>
      <VInput
        v-bind="questionAttrs"
        v-model="question"
        :maxlength="2048"
        type="textarea"
        :error="!!errors.question"
        rows="5"
        data-testid="ask-a-question-question-input"
      />
      <div v-if="errors.question" class="mt-1 text-sm text-red-800">{{ errors.question }}</div>
    </div>

    <VButton class="block ml-auto" type="submit" variant="copper" size="lg" data-testid="ask-a-question-submit">Submit Question</VButton>
  </form>
</template>

<script lang="ts" setup>
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import { z } from 'zod'
import ZodMessages from '@/zod_presets/messages'
import type { PropType } from 'vue'
import { type User } from '@/entities/user'
import type { GuestUser } from '@/entities/user'
import VInput from '@/components/VInput.vue'
import type { DeepReadonly } from 'vue'

const props = defineProps({
  user: {
    type: Object as PropType<DeepReadonly<User> | DeepReadonly<GuestUser> | undefined>,
    default: undefined,
  },
})

const emit = defineEmits(['on-submit'])

const Schema = z.object({
  name: z
    .string()
    .nonempty(ZodMessages.name)
    .default(props.user?.name ?? ''),
  email: z
    .string()
    .email(ZodMessages.email)
    .default((_isGuestUser(props.user) ? props.user?.email : (props.user as User)?.email_address) || ''),
  question: z.string().nonempty('Please enter a question').default(''),
})

const { handleSubmit, defineInputBinds, errors, defineField } = useForm<z.infer<typeof Schema>>({
  validationSchema: toTypedSchema(Schema),
})

const [name, nameAttrs] = defineField('name')
const [email, emailAttrs] = defineField('email')
const [question, questionAttrs] = defineField('question')

const isEditingEmail = ref(false)
const emailInputRef = ref<InstanceType<typeof VInput> | null>(null)
const isLoggedInUser = computed(() => !_isGuestUser(props.user))
const existingEmailAddress = computed(() => _isGuestUser(props.user) && props.user?.email)
const guestHasExistingEmailAddress = computed(() => !!existingEmailAddress.value)

const onSubmit = handleSubmit(values => emit('on-submit', values))

function onEditExistingEmail() {
  isEditingEmail.value = true

  nextTick(() => {
    ;(emailInputRef.value?.$el.firstElementChild as HTMLInputElement).focus()
  })
}

function _isGuestUser(user: DeepReadonly<User> | DeepReadonly<GuestUser> | undefined): user is GuestUser {
  return !user || !('email_address' in user)
}
</script>
